import React, { useMemo } from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button
} from "reactstrap";
import SuiteLayout from "../../_suite/SuiteLayout";
import LogisticsDashboard from "../../logistics";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import IntlMessages from "Util/IntlMessages";
import {
  SUITE_LOGISTICS_SHIPMENT_MAP,
  LOGISTICS_WAREHOUSE_UTIL_HEIGHT
} from "Constants/reactifyInlineStyles";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import { AsyncStateBoundary, SuitePageShell } from "../../_suite/primitives";

function WarehouseUtilHeatStrip({ utilPct }: { utilPct: number }) {
  const stripStyle = useMemo(
    () => ({
      ...LOGISTICS_WAREHOUSE_UTIL_HEIGHT,
      background: `linear-gradient(90deg, #1ba0f5 ${utilPct}%, #eee ${utilPct}%)`
    }),
    [utilPct]
  );
  return <div className="rounded" style={stripStyle} />;
}

function ShipmentsMapPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.logistics.listShipments(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.logistics.shipments" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(s => (
          <Card key={s.id} className="mb-4 border-0 shadow-sm overflow-hidden">
            <Row className="g-0">
              <Col md="5">
                <div
                  className="bg-secondary text-white-50 d-flex align-items-center justify-content-center h-100"
                  style={SUITE_LOGISTICS_SHIPMENT_MAP}
                >
                  Map: {s.lane}
                </div>
              </Col>
              <Col md="7">
                <CardBody>
                  <div className="d-flex justify-content-between">
                    <strong>{s.id}</strong>
                    <Badge
                      color={s.status === "delayed" ? "warning" : "success"}
                    >
                      {s.status}
                    </Badge>
                  </div>
                  <div className="small text-muted">{s.carrier}</div>
                  <div className="small">ETA {s.eta}</div>
                  <Button
                    tag={Link}
                    color="primary"
                    size="sm"
                    className="mt-2"
                    to={s.id}
                    relative="path"
                  >
                    Event log
                  </Button>
                </CardBody>
              </Col>
            </Row>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function ShipmentDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.logistics.getShipment(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.logistics.shipment-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <div className="ps-3 border-start border-primary">
            <div className="mb-3 small text-muted">Picked up</div>
            <div className="mb-3 small text-muted">In transit — {data.lane}</div>
            <div className="mb-3 small text-muted">ETA {data.eta}</div>
          </div>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function CarriersScorecardPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.logistics.listCarriers(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.logistics.carriers" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <table className="table table-striped bg-white shadow-sm rounded overflow-hidden">
          <thead>
            <tr>
              <th>Carrier</th>
              <th>OTD %</th>
              <th>SLA %</th>
              <th>Volume</th>
            </tr>
          </thead>
          <tbody>
            {(data || []).map(c => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>
                  <strong>{c.otdPct}</strong>
                </td>
                <td>{c.slaPct != null ? c.slaPct : "—"}</td>
                <td>{c.volume.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function WarehousesHeatPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.logistics.listWarehouses(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.logistics.warehouses" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          {(data || []).map(w => (
            <Col md="6" key={w.id} className="mb-4">
              <Card className="border-0 shadow-sm">
                <CardBody>
                  <h5>{w.code}</h5>
                  <p className="small text-muted">Utilization heat</p>
                  <WarehouseUtilHeatStrip utilPct={w.utilPct} />
                  <p className="mt-2 mb-0 small">
                    Throughput {w.throughput} units/hr
                  </p>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function ExceptionsQueuePage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.logistics.listExceptions(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.logistics.exceptions" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(x => (
          <Card
            key={x.id}
            className={`mb-3 border-0 shadow-sm ${
              x.severity === "high" ? "border-danger border" : ""
            }`}
          >
            <CardBody className="d-flex justify-content-between align-items-center">
              <div>
                <Badge className="me-2">{x.severity}</Badge>
                <strong>{x.id}</strong>
                <div>{x.type}</div>
                <div className="small text-muted">{x.lane}</div>
                {x.rootCause && (
                  <p className="small mb-0 mt-2 border-start border-primary ps-2">
                    <strong>Root cause:</strong> {x.rootCause}
                  </p>
                )}
              </div>
              <Button color="primary" size="sm">
                Resolve
              </Button>
            </CardBody>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

export function LogisticsSuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="logistics" />}>
        <Route index element={<LogisticsDashboard />} />
        <Route path="shipments" element={<ShipmentsMapPage />} />
        <Route path="shipments/:id" element={<ShipmentDetailPage />} />
        <Route path="carriers" element={<CarriersScorecardPage />} />
        <Route path="warehouses" element={<WarehousesHeatPage />} />
        <Route path="exceptions" element={<ExceptionsQueuePage />} />
      </Route>
    </Routes>
  );
}
